import { FC, ReactNode } from 'react'
import { useRevalidator } from 'react-router-dom'
import PageHeader from './PageHeader'
import PageLoadingSupport from '../PageLoadingSupport'
import FullPageLoader from '../FullPageLoader'
import SideNav from './SideNav'
import { SideNavItem } from './SideNavMenuItem'
import { Stack } from '@mui/material'
import useAppStore from '$appStore'

type PageLayoutProps = {
  children: ReactNode
  title?: string
  displayLeftTitle?: boolean
  displayDateFilter?: boolean
  customBottomLeftComponent?: string
  /**
   * If true, only the HTML title will be set, not the in-page title.
   * Note this also prevents the <PageHeader /> from being rendered.
   */
  htmlTitleOnly?: boolean
  /**
   * The items to display in the side navigation.
   */
  sideNavItems?: SideNavItem[]
}

/**
 * Main page layout component.
 * All pages should be presented via this component - i.e. a route should
 * render one of these components with a child component for whatever should
 * be shown in the main content area.
 *
 * Handle dynamic page titles via the usePageTitle hook in the page component.
 */
const PageLayout: FC<PageLayoutProps> = ({
  children,
  title,
  displayLeftTitle,
  displayDateFilter,
  customBottomLeftComponent,
  htmlTitleOnly = false,
  sideNavItems = undefined,
}) => {
  const revalidator = useRevalidator()

  const pageTitleFromStore = useAppStore((state) => state.pageTitle)

  // set the effective title, this should be the title, or if the pageTitleFromStore is not null, that takes precedence
  // If both are null, then the effective title is undefined
  const effectiveTitle =
    title ?? (pageTitleFromStore ? pageTitleFromStore : undefined)

  return (
    // Spacing property doesn't work in stack because of the render order
    // so we need to use the sx property
    <Stack
      direction="row"
      spacing={2}
      sx={{
        minHeight: (theme) =>
          `calc(100vh - ${theme.loaded.pageHeaderHeight}px)`,
        alignItems: 'stretch',
      }}
    >
      {sideNavItems && <SideNav sideNavItems={sideNavItems} />}

      <Stack
        sx={{
          // width is important here to constrain any content in this stack that
          // is wider than the space available in the parent stack (minus the
          // side nav width). Without this, we end up with a horizontal scroll.
          width: '100%',
          flex: 1,
          marginBottom:
            revalidator.state === 'loading'
              ? '0rem'
              : effectiveTitle
              ? '1.6rem'
              : '0rem',
        }}
      >
        {effectiveTitle && !htmlTitleOnly && (
          <PageHeader
            title={effectiveTitle}
            displayLeftTitle={displayLeftTitle}
            displayDateFilter={displayDateFilter}
            customBottomLeftComponent={customBottomLeftComponent}
          />
        )}
        <PageLoadingSupport title={effectiveTitle ?? undefined}>
          {revalidator.state === 'loading' ? <FullPageLoader /> : children}
        </PageLoadingSupport>
      </Stack>
    </Stack>
  )
}

export default PageLayout
